import { Link } from "react-router-dom";
import { Mail, Shield, FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandChip } from "@/components/BrandChip";

export default function TakedownPolicy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <BrandChip size={40} showWordmark />
          <Link to="/">
            <Button variant="ghost" className="text-white/70 hover:text-amber-500">
              <ArrowLeft className="w-4 h-4 mr-2" /> Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <span className="text-amber-500 text-sm font-semibold tracking-wider uppercase">
            Copyright &amp; Takedown Policy
          </span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
          Removal &amp; Attribution Requests
        </h1>

        <div className="prose prose-invert max-w-none space-y-6 text-white/75 leading-relaxed">
          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              About this site
            </h2>
            <p>
              <strong>North American Depots &amp; Towers</strong> is a non-commercial,
              volunteer-run preservation archive hosted by{" "}
              <span className="text-amber-500">Railstream</span> and dedicated to the memory of{" "}
              <span className="text-amber-500">Jack Marshall</span>, the original creator of{" "}
              <a href="https://www.depotmaps.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                depotmaps.com
              </a>
              . Our purpose is to keep the historical record of North American railroad passenger
              depots and interlocking towers accessible to railfans, historians, and future
              generations.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              How photos got here
            </h2>
            <p>Our archive sources photos from:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong className="text-white">depotmaps.com</strong> — Jack Marshall's original
                photo archive, preserved here so it isn't lost as the original site goes offline.
              </li>
              <li>
                <strong className="text-white">northamericaninterlockings.com</strong> — interlocking
                tower photos compiled over decades by the railfan community.
              </li>
              <li>
                <strong className="text-white">Wikipedia / Wikimedia Commons</strong> — used under
                CC&nbsp;BY-SA and equivalent licenses, with attribution.
              </li>
              <li>
                <strong className="text-white">User submissions</strong> — photos uploaded by
                volunteers who have rights to share them.
              </li>
            </ul>
            <p className="mt-3">
              Every photo on this site shows its source/credit ribbon at the bottom. Where we know
              the original photographer or collection, we name them.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              Removal requests — fast and friendly
            </h2>
            <p>
              If you own the copyright to a photo here (or you're representing the photographer's
              estate / collection) and you want it removed, recredited, or watermarked, we will act
              within <strong className="text-amber-500">48&nbsp;hours</strong> of receiving your
              request. No legal forms needed for the first request.
            </p>
            <p>
              Use any one of these channels:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                Hover over the photo on its page and click the <strong className="text-amber-500">Report</strong> button.
              </li>
              <li>
                Email{" "}
                <a href="mailto:takedown@railstream.net" className="text-amber-400 hover:underline">
                  takedown@railstream.net
                </a>{" "}
                — include a link to the photo and a sentence about your relationship to the work.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              Formal DMCA notice
            </h2>
            <p>
              If you'd prefer a formal DMCA-style takedown, please include the following in your
              email so we can process it without follow-up:
            </p>
            <ol className="list-decimal pl-6 space-y-2 mt-2">
              <li>A description of the copyrighted work claimed to be infringed.</li>
              <li>The URL(s) of the photo(s) on this site you want removed.</li>
              <li>Your contact information (name, email, phone optional, address optional).</li>
              <li>
                A statement that you have a good-faith belief that the use is not authorized by the
                copyright owner, its agent, or the law.
              </li>
              <li>
                A statement, under penalty of perjury, that the information in your notice is
                accurate and that you are the copyright owner or authorized to act on the owner's behalf.
              </li>
              <li>Your electronic or physical signature.</li>
            </ol>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              Counter-notice
            </h2>
            <p>
              If we remove content based on a notice and you believe the removal was a mistake or
              misidentification, you may send a counter-notice to the same email. We follow the
              standard DMCA counter-notice procedure (17&nbsp;U.S.C. §&nbsp;512(g)).
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-white mt-8 mb-3">
              Reuse of this archive
            </h2>
            <p>
              This site is not the rights-holder for most of the photos in the archive. Please do
              not assume an image here is free to reuse — track down the original photographer or
              collection (we credit them where we can) and ask them directly.
            </p>
          </section>

          <section className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 mt-10 flex gap-3">
            <Mail className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-400 mb-1">Single point of contact</p>
              <p className="text-white/70">
                For any copyright, attribution, privacy, or content concern — please reach{" "}
                <a href="mailto:takedown@railstream.net" className="text-amber-400 hover:underline">
                  takedown@railstream.net
                </a>
                . A real person answers.
              </p>
            </div>
          </section>

          <section className="text-xs text-white/40 mt-8 border-t border-white/5 pt-6">
            <p className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              This policy was last updated in February 2026. It may evolve; check back occasionally.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
